import { G } from "@mobily/ts-belt";
import {
  capitalizeWord,
  dateFormatter,
  numberFormatter,
  percentFormatter,
} from "../../util";
import { currencyFormatter } from "../../util/formatCurrency";
import { pluralizeEnglish } from "../../util/text";
import { addFilters } from "../GetPaid/GetPaidCardList/utils";
import { orderStatus, shippingStatus } from "../SalesOverview/filters";
import { dashboardPath } from "../../domain/dashboardPaths";

const EM_DASH = "—";

const isMissing = G.isNullable;

const countOf = (value, noun) => `${value} ${pluralizeEnglish(noun, value)}`;

export const formatMoney = (cents) =>
  isMissing(cents) ? EM_DASH : currencyFormatter.format(cents / 100);

export const formatCount = (value) =>
  isMissing(value) ? EM_DASH : numberFormatter.format(value);

export const formatAge = (days) =>
  isMissing(days) ? EM_DASH : days === 0 ? "Today" : countOf(days, "day");

export const formatDate = (iso) =>
  iso ? dateFormatter.format(new Date(iso)) : EM_DASH;

/*
 * Collections aggregate other shops' products, and agent and coordinator shops
 * sell nothing of their own — the same condition `isNotGroupAgentOrTc` in
 * src/navigation/config.js guards the Sales Overview link with. The drawer
 * reports an order pipeline exactly when that link is on offer.
 */
export const sellsOwnProducts = (influencer) =>
  Boolean(influencer) &&
  !influencer.isGroupShop &&
  !influencer.isAgentShop &&
  !influencer.isTcShop;

const SHOP_TYPE_LABELS = {
  talent: "Talent",
  collection: "Collection",
  agent: "Agent",
  talent_coordinator: "Talent coordinator",
};

const failingCount = (payload) =>
  (payload?.setup?.checks ?? []).filter((check) => check.status === "fail")
    .length;

// Every chip pairs an icon with its label so status is never carried by colour
// alone, and only states worth acting on are rendered.
export const statusChips = (payload) => {
  if (!payload) {
    return [];
  }

  const { isOpen, isHidden, isSearchable, isLive } = payload.visibility;
  const failing = failingCount(payload);

  return [
    isOpen
      ? { key: "open", label: "Open", tone: "success", icon: "open" }
      : { key: "closed", label: "Closed", tone: "neutral", icon: "closed" },
    isLive && { key: "live", label: "Live", tone: "info", icon: "live" },
    isHidden && {
      key: "hidden",
      label: "Hidden",
      tone: "warning",
      icon: "hidden",
    },
    !isSearchable && {
      key: "searchable",
      label: "Not searchable",
      tone: "warning",
      icon: "hidden",
    },
    failing > 0 && {
      key: "setup",
      label: `${failing} setup ${failing === 1 ? "issue" : "issues"}`,
      tone: "critical",
      icon: "problem",
    },
  ].filter(Boolean);
};

const ageingHint = ({ over7, over14, over30 }) =>
  over7 + over14 + over30 === 0
    ? null
    : `${over7} over 7d · ${over14} over 14d · ${over30} over 30d`;

export const orderStats = (payload) => {
  const orders = payload?.orders;

  if (!orders) {
    return [];
  }

  return [
    {
      label: "Awaiting fulfillment",
      value: formatCount(orders.awaitingFulfillment),
      hint: "Not signed or completed by the talent",
    },
    {
      label: "Overdue",
      value: formatCount(orders.overdue),
      hint: orders.overdue > 0 ? "Past the completion deadline" : null,
      tone: orders.overdue > 0 ? "critical" : undefined,
    },
    {
      label: "Oldest pending",
      value: formatAge(orders.oldestPendingDays),
      hint: null,
    },
    {
      label: "Signed, not received",
      value: formatCount(orders.signedNotReceived.total),
      hint: ageingHint(orders.signedNotReceived),
    },
    {
      label: "Image approvals pending",
      value: formatCount(orders.imageApprovalsPending),
      hint: null,
    },
  ];
};

const NO_BALANCES = { availableBalance: 0, pending: 0, totalPaid: 0 };

/*
 * The money shown is the payout account's own on this shop — its
 * autograph_purchase_split rows for this influencer_id — not the shop's total
 * across every split. A newly assigned account has earned nothing yet, which is
 * zero rather than missing.
 */
const balancesFor = ({ accounts = [], shopSplit }) =>
  accounts.find((account) => account.userId === shopSplit?.user_id) ??
  NO_BALANCES;

export const payoutStats = (payload) => {
  const payout = payload?.payout;

  if (!payout) {
    return [];
  }

  const { shopSplit } = payout;
  const balances = balancesFor(payout);

  return [
    // The one account the shop currently pays. A shop with none is exactly what
    // the shop_split setup check reports, so it is named as a finding.
    {
      label: "Payout account",
      value: shopSplit?.email ?? "No payout account",
      hint: null,
      tone: shopSplit ? undefined : "critical",
    },
    {
      label: "Share",
      value: isMissing(shopSplit?.percent_cut)
        ? EM_DASH
        : percentFormatter.format(Number(shopSplit.percent_cut)),
      hint: null,
    },
    {
      label: "Available balance",
      value: formatMoney(balances.availableBalance),
      hint: "This account, on this shop; claimable now",
    },
    {
      label: "Pending",
      value: formatMoney(balances.pending),
      hint: "Earned, but not yet eligible to pay out",
    },
    {
      label: "Paid to date",
      value: formatMoney(balances.totalPaid),
      hint: null,
    },
  ];
};

export const peopleStats = (payload) => {
  const people = payload?.people;

  if (!people) {
    return [];
  }

  const { owner, accessCount } = people;

  return [
    {
      label: "Owner",
      value: owner ? owner.name ?? owner.email : "No owner",
      hint: null,
      tone: owner ? undefined : "critical",
    },
    { label: "Owner email", value: owner?.email ?? EM_DASH, hint: null },
    {
      label: "Owner last login",
      // An owner who has never signed in explains a stalled shop, so say so
      // rather than leaving a dash that reads as missing data.
      value: owner
        ? owner.lastLoginAt
          ? formatDate(owner.lastLoginAt)
          : "Never logged in"
        : EM_DASH,
      hint: null,
      tone: owner && !owner.lastLoginAt ? "warning" : undefined,
    },
    {
      label: "Owner last active",
      value: owner?.lastActivityAt ? formatDate(owner.lastActivityAt) : EM_DASH,
      hint: null,
    },
    { label: "Users with access", value: formatCount(accessCount), hint: null },
  ];
};

export const shopStats = (payload) => {
  if (!payload) {
    return [];
  }

  const { shop, visibility } = payload;

  return [
    { label: "Shop ID", value: String(shop.influencerId), hint: null },
    {
      label: "Type",
      value: SHOP_TYPE_LABELS[shop.type] ?? shop.type,
      hint: null,
    },
    {
      label: "Products",
      value: `${formatCount(visibility.activeProductCount)} of ${formatCount(
        visibility.totalProductCount,
      )}`,
      hint: "Active of non-archived",
    },
    {
      label: "Completion deadline",
      value: isMissing(shop.completionDeadline)
        ? EM_DASH
        : countOf(shop.completionDeadline, "day"),
      hint: null,
    },
    {
      label: "Fulfillment speed",
      value: shop.fulfillmentSpeed
        ? capitalizeWord(shop.fulfillmentSpeed)
        : EM_DASH,
      hint: null,
    },
    { label: "Created", value: formatDate(shop.created), hint: null },
  ];
};

const SALES_FILTERS = {
  incomplete: [orderStatus.INCOMPLETE],
  notReceived: [orderStatus.COMPLETED, shippingStatus.NOT_RECEIVED],
  received: [shippingStatus.RECEIVED],
};

export const salesOverviewHref = (route, filterKey) =>
  route && SALES_FILTERS[filterKey]
    ? `${dashboardPath({ shop: route, page: "sales" })}?${addFilters(
        ...SALES_FILTERS[filterKey],
      )}`
    : null;
